import React, { useState, useEffect, ReactNode, useMemo } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  Placement,
} from '@floating-ui/react-dom';
import { SelectContext } from './SelectContext';

interface SelectProps {
  children: ReactNode;
  value: string;
  onChange: (value: string, label?: string) => void;
  direction?: Placement;
  options?: { value: string; label: string }[];
}

export const Select: React.FC<SelectProps> = ({
  children,
  value,
  onChange,
  direction = 'bottom-start',
  options,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { x, y, strategy, refs, update } = useFloating({
    placement: direction,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (isOpen) {
      update();
    }
  }, [isOpen, update]);

  const optionsMap = useMemo(() => {
    return options?.reduce((acc, cur) => {
      acc[cur.value] = cur.label;
      return acc;
      // TODO: refactor without type assertion, or add comment
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    }, {} as Record<string, string>);
  }, [options]);

  // Close the dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const referenceElement = refs.reference.current;
      const floatingElement = refs.floating.current;

      if (
        referenceElement &&
        'contains' in referenceElement &&
        // Type narrowing to ensure contains exists (only exists when !VirtualElement)
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !referenceElement.contains(event.target as Node) &&
        floatingElement &&
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !floatingElement.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, refs.reference, refs.floating]);

  return (
    <SelectContext.Provider
      value={{
        value,
        onChange,
        isOpen,
        setIsOpen,
        refs,
        x,
        y,
        strategy,
        optionsMap,
      }}
    >
      <div>{children}</div>
    </SelectContext.Provider>
  );
};
